/**
 * Readers for the tenant's magico `configs` bag.
 *
 * Every entry is a free-form string, so a flag arrives as `"false"` — truthy if tested
 * directly. Nothing validates the CMS response either, so these take `unknown` and never
 * throw; any bad value falls back to the caller's default.
 */

const TRUE_TOKENS = new Set(["true", "1", "yes", "on", "enabled"]);
const FALSE_TOKENS = new Set(["false", "0", "no", "off", "disabled", ""]);


export const readConfigFlag = (value: unknown, fallback = false): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return Number.isFinite(value) ? value !== 0 : fallback;
  if (typeof value !== "string") return fallback;

  const token = value.trim().toLowerCase();
  if (TRUE_TOKENS.has(token)) return true;
  if (FALSE_TOKENS.has(token)) return false;

  return fallback;
};

/** Parses a config holding stringified JSON (`top_CTA`, `home_pager_tabs`). */
export const readConfigJson = <T>(value: unknown, fallback: T): T => {
  if (typeof value === "object" && value !== null) return value as T;
  if (typeof value !== "string") return fallback;

  const trimmed = value.trim();
  if (!trimmed) return fallback;

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return fallback;
  }

  // `T` is only an assertion — a scalar typed as an object breaks on first field access.
  if (typeof parsed !== "object" || parsed === null) {
    return fallback;
  }

  return parsed as T;
};
